import React, { useState } from "react";
import { Formik, Form } from "formik";
import {
  Box, Card, CardContent, Typography, Divider,
  Chip, IconButton, Tooltip, Collapse, Alert,
} from "@mui/material";
import ContentCopyIcon  from "@mui/icons-material/ContentCopy";
import OpenInNewIcon    from "@mui/icons-material/OpenInNew";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import FormikControl   from "../components/FormikControl";
import { InitSchema }  from "../schemas/InitSchema";
import { PaymentService } from "../services/PaymentService";

/** Generate orderId in format FROMCHECKOUT{DDMMYYHHMMSS} */
const generateOrderId = () => {
  const now = new Date();
  const dd  = String(now.getDate()).padStart(2, "0");
  const mm  = String(now.getMonth() + 1).padStart(2, "0");
  const yy  = String(now.getFullYear()).slice(-2);
  const hh  = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  const ss  = String(now.getSeconds()).padStart(2, "0");
  return `FROMCHECKOUT${dd}${mm}${yy}${hh}${min}${ss}`;
};

const INITIAL_VALUES = {
  appId:           "",
  secretKey:       "",
  orderId:         generateOrderId(),
  amount:          "10.0",
  custEmail:       "test@example.com",
  custPhone:       "9999999999",
  custFirstName:   "Customer",
  currencyCode:    "356",
  returnUrl:       "https://pgapi.green.com/response-page",
  transactionType: "SALE",
  productDesc:     "UPI intent for buy the product",
  userIpAddress:   "179.1.44.89",
};

const FIELDS = [
  { name: "appId",           label: "App ID",               placeholder: "Enter your App ID"           },
  { name: "secretKey",       label: "Secret Key",           placeholder: "Enter your Secret Key"       },
  { name: "orderId",         label: "Order ID",             placeholder: "FROMCHECKOUT{DDMMYYHHMMSS}"   },
  { name: "amount",          label: "Amount",               placeholder: "e.g. 10.0"                   },
  { name: "custEmail",       label: "Email",                placeholder: "customer@example.com"         },
  { name: "custPhone",       label: "Customer Phone",       placeholder: "10-digit phone number"        },
  { name: "custFirstName",   label: "First Name",           placeholder: "Customer first name"          },
  { name: "currencyCode",    label: "Currency Code",        placeholder: "e.g. 356 for INR"             },
  { name: "returnUrl",       label: "Return URL",           placeholder: "https://your-site.com/..."    },
  { name: "transactionType", label: "Transaction Type",     placeholder: "e.g. SALE"                    },
  { name: "productDesc",     label: "Product Description",  placeholder: "Enter product description"    },
  { name: "userIpAddress",   label: "User IP Address",      placeholder: "e.g. 179.1.44.89"            },
];

const InitPage = () => {
  const [linkResult, setLinkResult]   = useState(null);   // { paymentLink, orderId, amount, transactionId }
  const [copied, setCopied]           = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLinkResult(null);
      const res = await PaymentService.generateOrderLink(values);

      // API returns a flat object — paymentLink is at the top level
      if (res?.statusCode === 200 && res?.paymentLink) {
        setLinkResult({
          paymentLink:   res.paymentLink,
          orderId:       res.orderId,
          amount:        res.amount,
          transactionId: res.transactionId,
          message:       res.message || "Payment Link Generated Successfully",
        });
        toast.success("Payment link generated!");
      } else {
        toast.error(res?.message || res?.error || "Failed to generate payment link");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    if (!linkResult?.paymentLink) return;
    navigator.clipboard.writeText(linkResult.paymentLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      p={3}
      sx={{ background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)" }}
    >
      {/* ── Result Card ───────────────────────────────────────────── */}
      <Collapse in={!!linkResult} sx={{ width: "100%", maxWidth: 820, mb: 2 }}>
        {linkResult && (
          <Card
            sx={{
              borderRadius: "20px",
              boxShadow: "0 8px 32px rgba(21,184,109,0.18)",
              border: "1px solid #bbf7d0",
              background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              {/* Header */}
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <CheckCircleIcon sx={{ color: "#15b86d", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700} color="#15803d">
                  {linkResult.message}
                </Typography>
              </Box>

              {/* Meta chips */}
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip
                  size="small"
                  label={`Order: ${linkResult.orderId}`}
                  sx={{ background: "#bbf7d0", color: "#166534", fontWeight: 600 }}
                />
                <Chip
                  size="small"
                  label={`₹ ${linkResult.amount}`}
                  sx={{ background: "#d1fae5", color: "#065f46", fontWeight: 600 }}
                />
                {linkResult.transactionId && (
                  <Chip
                    size="small"
                    label={`TxnID: ${linkResult.transactionId}`}
                    sx={{ background: "#e0f2fe", color: "#0369a1", fontWeight: 600 }}
                  />
                )}
              </Box>

              {/* Payment link box */}
              <Alert
                severity="success"
                icon={false}
                sx={{
                  borderRadius: "10px",
                  border: "1px solid #86efac",
                  background: "#fff",
                  alignItems: "center",
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      wordBreak: "break-all",
                      fontFamily: "monospace",
                      color: "#1e3a5f",
                      fontSize: "0.78rem",
                    }}
                  >
                    {linkResult.paymentLink}
                  </Typography>

                  <Tooltip title={copied ? "Copied!" : "Copy link"}>
                    <IconButton size="small" onClick={handleCopy} sx={{ color: "#15b86d" }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Open in new tab">
                    <IconButton
                      size="small"
                      component="a"
                      href={linkResult.paymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "#0369a1" }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Alert>
            </CardContent>
          </Card>
        )}
      </Collapse>

      {/* ── Form Card ─────────────────────────────────────────────── */}
      <Card
        sx={{
          maxWidth: 820,
          width: "100%",
          borderRadius: "20px",
          boxShadow: "0 8px 32px rgba(15,23,42,0.12)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" fontWeight={700} color="#1f2937" mb={0.5}>
            Generate Payment Link
          </Typography>
          <Typography variant="body2" color="#6b7280" mb={3}>
            Fill in the order details to generate a hosted payment link.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Formik
            initialValues={INITIAL_VALUES}
            validationSchema={InitSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isValid, dirty, isSubmitting }) => (
              <Form>
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                  gap={2.5}
                >
                  {FIELDS.map((f) => (
                    <FormikControl
                      key={f.name}
                      control="textfield"
                      name={f.name}
                      label={f.label}
                      placeholder={f.placeholder}
                      requiredField
                    />
                  ))}
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={1.5} mt={4}>
                  <FormikControl
                    control="button"
                    label="Reset"
                    type="reset"
                    color="inherit"
                    variant="outlined"
                    sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                  />
                  <FormikControl
                    control="button"
                    label={isSubmitting ? "Generating…" : "Generate Link"}
                    type="submit"
                    variant="contained"
                    disabled={!dirty || !isValid || isSubmitting}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      px: 4,
                      background: "#15b86d",
                      "&:hover":    { background: "#13a360" },
                      "&:disabled": { background: "#e5e7eb", color: "#9ca3af" },
                    }}
                  />
                </Box>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InitPage;
